import React from 'react';
import { ContentItem, Activity, TouchPoint, PainPoint, Opportunity } from '@/types/journey';
import { Button } from '@/components/ui/button';
import { Plus, FileText, Wrench, Link2, Hash, AlignLeft, X } from 'lucide-react';
import ActivityField from './ActivityField';
import TouchPointField from './TouchPointField';
import PainPointField from './PainPointField';
import OpportunityField from './OpportunityField';
import EditableField from './EditableField';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DynamicContentFieldProps {
  contentItems: ContentItem[];
  onContentUpdate: (contentItems: ContentItem[]) => void;
}

// Define the field type order
const FIELD_TYPE_ORDER = {
  'description': 1,
  'activities': 2,
  'touchPoints': 3,
  'painPoints': 4,
  'opportunities': 5
};

// Field type options - ordered for the dropdown menu
const CONTENT_FIELD_TYPES = [
  { id: 'description', label: 'Description', icon: <AlignLeft className="h-4 w-4" /> },
  { id: 'activities', label: 'Activities', icon: <Hash className="h-4 w-4" /> },
  { id: 'touchPoints', label: 'Touch Points', icon: <Link2 className="h-4 w-4" /> },
  { id: 'painPoints', label: 'Pain Points', icon: <Wrench className="h-4 w-4" /> },
  { id: 'opportunities', label: 'Opportunities', icon: <FileText className="h-4 w-4" /> },
];

const DynamicContentField: React.FC<DynamicContentFieldProps> = ({
  contentItems,
  onContentUpdate
}) => {
  // Add a new content field
  const handleAddContentField = (type: 'activities' | 'description' | 'painPoints' | 'opportunities' | 'touchPoints') => {
    let newContent: any;
    
    switch (type) {
      case 'activities':
        newContent = [] as Activity[];
        break;
      case 'description':
        newContent = '';
        break;
      case 'painPoints':
        newContent = [] as PainPoint[];
        break;
      case 'opportunities':
        newContent = [] as Opportunity[];
        break;
      case 'touchPoints':
        newContent = [] as TouchPoint[];
        break;
    }
    
    const newItem: ContentItem = {
      id: `content-${type}-${Date.now()}`,
      type,
      content: newContent
    };
    
    onContentUpdate([...contentItems, newItem]);
  };
  
  // Remove a content field
  const handleRemoveContentField = (id: string) => {
    onContentUpdate(contentItems.filter(item => item.id !== id));
  };
  
  // Update a content field
  const handleUpdateContentField = (id: string, content: any) => {
    const updatedItems = contentItems.map(item => {
      if (item.id === id) {
        return { ...item, content };
      }
      return item;
    });
    
    onContentUpdate(updatedItems);
  };

  // Check if a field type is already in use
  const isFieldTypeInUse = (type: string) => {
    return contentItems.some(item => item.type === type);
  };

  // Render the appropriate field based on type
  const renderContentField = (item: ContentItem) => {
    switch (item.type) {
      case 'activities':
        return (
          <div className="relative group" key={item.id}>
            <ActivityField
              label="Activities"
              activities={item.content}
              onChange={(activities) => handleUpdateContentField(item.id, activities)}
              placeholder="Click to add user activities for this stage..."
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveContentField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      case 'description':
        return (
          <div className="relative group" key={item.id}>
            <EditableField
              label="Description"
              value={item.content}
              placeholder="Click to describe what happens during this stage of the journey"
              tooltipText="Describe what happens during this stage of the user journey"
              onChange={(value) => handleUpdateContentField(item.id, value)}
              lines={3}
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveContentField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      case 'painPoints':
        return (
          <div className="relative group" key={item.id}>
            <PainPointField
              label="Pain Points"
              painPoints={Array.isArray(item.content) ? item.content : []}
              onChange={(painPoints) => handleUpdateContentField(item.id, painPoints)}
              placeholder="Click to add frustrations or challenges users face at this stage"
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveContentField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      case 'opportunities':
        return (
          <div className="relative group" key={item.id}>
            <OpportunityField
              label="Opportunities"
              opportunities={Array.isArray(item.content) ? item.content : []}
              onChange={(opportunities) => handleUpdateContentField(item.id, opportunities)}
              placeholder="Click to suggest ways to improve the user experience at this stage"
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveContentField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      case 'touchPoints':
        return (
          <div className="relative group" key={item.id}>
            <TouchPointField
              label="Touch Points"
              touchPoints={item.content}
              onChange={(touchPoints) => handleUpdateContentField(item.id, touchPoints)}
              placeholder="Click to add interaction touch points for this stage..."
            />
            <button
              type="button"
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleRemoveContentField(item.id)}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Sort content items according to the defined order
  const sortedContentItems = [...contentItems].sort((a, b) => {
    const orderA = FIELD_TYPE_ORDER[a.type] || 999;
    const orderB = FIELD_TYPE_ORDER[b.type] || 999;
    return orderA - orderB;
  });

  return (
    <div className="space-y-3 flex-1">
      {/* Content fields - sorted by defined order */}
      {sortedContentItems.map(renderContentField)}
      
      {/* Add field button */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full border border-dashed border-muted-foreground/20 bg-muted/20 hover:bg-muted/40 text-xs justify-center py-1 h-auto"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add content field
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {CONTENT_FIELD_TYPES.map((fieldType) => (
            <DropdownMenuItem
              key={fieldType.id}
              disabled={isFieldTypeInUse(fieldType.id)}
              onClick={() => handleAddContentField(fieldType.id as any)}
              className={isFieldTypeInUse(fieldType.id) ? 'opacity-50 cursor-not-allowed' : ''}
            >
              <div className="flex items-center">
                {fieldType.icon}
                <span className="ml-2">{fieldType.label}</span>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default DynamicContentField; 